import React, { useState, useEffect } from 'react';
import DatabaseManager from './DatabaseManager';
import DataTable from './DataTable';
import InfoModal from './InfoModal';
import Tabs from './Tabs';
import GuoNei from './GuoNei';
import Settings from './Settings';

function App() {
  const [activeTab, setActiveTab] = useState('国际');
  const [selectedDb, setSelectedDb] = useState<string>('');
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [modalData, setModalData] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [selectedProcessedFile, setSelectedProcessedFile] = useState<string | null>(null);
  const [modalDownloadUrl, setModalDownloadUrl] = useState<string | undefined>(undefined);
  const [taskId, setTaskId] = useState<string | null>(null); // New: Store the task ID
  // New state variables for generated file paths and CSV display control
  const [generatedMdPath, setGeneratedMdPath] = useState<string | null>(null);
  const [generatedCsvKeepPath, setGeneratedCsvKeepPath] = useState<string | null>(null);
  const [generatedCsvNotKeepPath, setGeneratedCsvNotKeepPath] = useState<string | null>(null);
  const [generatedDocxPath, setGeneratedDocxPath] = useState<string | null>(null);
  const [showCsvData, setShowCsvData] = useState<'keep' | 'not_keep' | null>(null);

  useEffect(() => {
    // Attempt to load selectedProcessedFile from localStorage on initial load
    const storedSelection = localStorage.getItem('selectedProcessedFile');
    if (storedSelection) {
      setSelectedProcessedFile(storedSelection);
    }
  }, []);

  const handleSelectDatabase = async (dbName: string) => {
    // If there's an existing task, attempt to clean up its files before proceeding
    if (taskId) {
      try {
        await fetch(`/cleanup_task_results/${taskId}`, { method: 'DELETE' });
        console.log(`Cleaned up files for task ${taskId}`);
      } catch (error) {
        console.error(`Failed to clean up files for task ${taskId}:`, error);
        // Continue even if cleanup fails, as it's not critical for the main flow
      }
    }

    setSelectedDb(dbName);
    setTableData([]); // Clear previous data
    // Reset generation-related states when a new database is selected
    setGenerationStatus('');
    setTaskId(null);
    setGeneratedMdPath(null);
    setGeneratedCsvKeepPath(null);
    setGeneratedCsvNotKeepPath(null);
    setGeneratedDocxPath(null);
    setShowCsvData(null); // Also reset any CSV display

    try {
      const response = await fetch(`/databases/${dbName}/com_el`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setTableData(data);
      } else {
        console.error("Fetched data is not an array:", data);
        setTableData([]); // Set to empty array on unexpected data format
      }
    } catch (error) {
      console.error("Failed to fetch table data:", error);
      setTableData([]); // Also set to empty array on fetch error
    }
  };

  const handleShowOrbit = async (ntcId: number) => {
    setModalDownloadUrl(undefined); // Reset download URL for non-report modals
    try {
      const response = await fetch(`/databases/${selectedDb}/orbit/${ntcId}`);
      const data = await response.json();
      setModalData(data.details);
      setModalTitle(`轨道详情 NTC_ID: ${ntcId} (${data.type})`);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch orbit details:", error);
    }
  };

  const handleShowGrp = async (ntcId: number) => {
    setModalDownloadUrl(undefined); // Reset download URL for non-report modals
    try {
      const response = await fetch(`/databases/${selectedDb}/grp/${ntcId}`);
      const data = await response.json();
      setModalData(data);
      setModalTitle(`频率组详情 NTC_ID: ${ntcId}`);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
    }
  };

  const handleGenerateComments = async () => {
    if (selectedIds.length === 0) {
      alert('请选择至少一个条目以生成评论。');
      return;
    }
    if (!selectedProcessedFile) {
      alert('请在“自家网络设置”中选择一个已处理的网络文件。');
      return;
    }
    setGenerationStatus('正在生成评论...');
    try {
      const response = await fetch('/generate_comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          db_name: selectedDb,
          ntc_ids: selectedIds,
          selected_network: selectedProcessedFile // Pass the selected processed file
        }),
      });
      const data = await response.json();
      setGenerationStatus(`评论生成已开始 (任务ID: ${data.task_id})...`);
      setTaskId(data.task_id); // Set the task ID here
      setModalDownloadUrl(undefined); // Reset before polling starts for this task

      // Poll for task completion
      const intervalId = setInterval(async () => {
        try {
          const taskResponse = await fetch(`/tasks/${data.task_id}`);
          const taskData = await taskResponse.json();
          if (taskData.status === 'completed') {
            clearInterval(intervalId);
            setGenerationStatus('评论生成完成。');

            const generatedMdPath = taskData.result?.generated_md_path ?? null;
            const generatedCsvKeepPath = taskData.result?.generated_csv_keep_path ?? null;
            const generatedCsvNotKeepPath = taskData.result?.generated_csv_notkeep_path ?? null;
            const generatedDocxPath = taskData.result?.generated_docx_path ?? null; // Capture DOCX path

            setGeneratedMdPath(generatedMdPath);
            setGeneratedCsvKeepPath(generatedCsvKeepPath);
            setGeneratedCsvNotKeepPath(generatedCsvNotKeepPath);
            setGeneratedDocxPath(generatedDocxPath);

            // Fetch and display the markdown file using the full path
            if (generatedMdPath) {
              // Extract just the filename for display/download links
              const mdFilename = generatedMdPath.split('/').pop();
              const mdResponse = await fetch(`/results/${mdFilename}?v=${Date.now()}`); 
              const markdown = await mdResponse.text();
              setModalData(markdown);
              setModalTitle(mdFilename); 
              setModalDownloadUrl(`/results/${mdFilename}`); 
              setIsModalOpen(true);
            } else {
                console.error("Generated MD path not found in task result.");
                setModalTitle("错误");
                setModalData("无法获取Markdown报告路径。");
                setIsModalOpen(true);
            }
          } else if (taskData.status === 'failed') {
            clearInterval(intervalId);
            setGenerationStatus(`评论生成失败: ${taskData.error}`);
            setModalDownloadUrl(undefined); // Reset download URL on failure
          }
        } catch (error) {
          clearInterval(intervalId);
          setGenerationStatus('获取任务状态失败。');
          console.error("Task status error:", error);
          setModalDownloadUrl(undefined); // Reset download URL on error
        }
      }, 2000); // Poll every 2 seconds
    } catch (error) {
      setGenerationStatus('启动评论生成失败。');
      console.error("Comment generation error:", error);
      setModalDownloadUrl(undefined); // Reset download URL on generation start failure
    }
  };

  // Function to fetch and display CSV data in the modal
  async function fetchCsvData(fileType: 'keep' | 'not_keep') {
    let filename: string | null = null;
    if (fileType === 'keep') {
      filename = (generatedCsvKeepPath?.split('/').pop() ?? null);
    } else {
      filename = (generatedCsvNotKeepPath?.split('/').pop() ?? null);
    }

    if (!filename) {
        alert("无法获取CSV数据，因为路径不存在。");
        return;
    }
    
    try {
      const response = await fetch(`/results/parsed-csv/${filename}?v=${Date.now()}`);
      const data = await response.json();

      if (Array.isArray(data)) {
        setModalData(data); // Display data in the modal
        setModalTitle(`${fileType === 'keep' ? '包含的重叠' : '舍弃的重叠'} CSV`);
        setModalDownloadUrl(`/results/parsed-csv/${filename}`); // Enable download from modal
        setIsModalOpen(true);
      } else {
        console.error("Fetched data is not an array:", data);
        setModalData("未能加载CSV数据。");
        setModalTitle("CSV数据错误");
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error(`Failed to fetch ${filename}:`, error);
      setModalData(`未能加载CSV数据: ${error}`);
      setModalTitle("CSV数据错误");
      setIsModalOpen(true);
    }
  }

  const handleDownloadAllResults = async () => {
    if (!taskId) return; // Ensure taskId is available
    
    try {
      const response = await fetch(`/download_results/${taskId}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${taskId}_results.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // No page refresh needed unless specifically desired after download
      // window.location.reload(); 

    } catch (error: unknown) {
      console.error('Download error:', error);
      if (error instanceof Error) {
        alert(`下载失败: ${error.message}`);
      } else {
        alert(`下载失败: ${String(error)}`);
      }
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>协调意见助手 {selectedProcessedFile ? ` - ${selectedProcessedFile.replace(/^s6output\./, '').replace(/\.csv$/, '')}` : ''}</h1>
      </header>
      <main>
        <Tabs activeTab={activeTab} onTabClick={setActiveTab} />
        {activeTab === '国际' && (
          <>
            <DatabaseManager onSelectDatabase={handleSelectDatabase} />
            <hr style={{ margin: '20px 0' }} />
            {selectedDb && (
              <div>
                <h2>{selectedDb}的数据</h2>
                {(generationStatus === '评论生成完成。' || generationStatus.startsWith('评论生成失败')) ? (
                  // Display buttons after generation attempt
                  <div>
                    {/* Button to download all results as a zip */}
                    <button 
                      onClick={handleDownloadAllResults} 
                      disabled={!taskId} // Only enabled if a taskId exists
                      style={{ marginRight: '10px' }}
                    >
                      下载所有结果 (Zip)
                    </button>

                    {generatedMdPath && (
                      <button 
                        onClick={() => {
                          if (generatedMdPath) {
                            // Ensure mdFilename is always a string for setModalTitle
                            const mdFilename = generatedMdPath.split('/').pop() || '';
                            fetch(`/results/${mdFilename}`).then(res => res.text()).then(markdown => {
                              setModalData(markdown);
                              setModalTitle(mdFilename);
                              setModalDownloadUrl(`/results/${mdFilename}`); // Correct download URL for single MD file
                              setIsModalOpen(true);
                            });
                          }
                        }}
                        style={{ marginRight: '10px' }}
                      >
                        查看Markdown报告
                      </button>
                    )}
                    {generatedCsvKeepPath && (
                      <button 
                        onClick={() => fetchCsvData('keep')}
                        style={{ marginRight: '10px' }}
                      >
                        查看包含的重叠 CSV
                      </button>
                    )}
                    {generatedCsvNotKeepPath && (
                      <button 
                        onClick={() => fetchCsvData('not_keep')}
                        style={{ marginRight: '10px' }}
                      >
                        查看舍弃的重叠 CSV
                      </button>
                    )}
                  </div>
                ) : (
                  // Display generate button during initial or in-progress state
                  <button onClick={handleGenerateComments} disabled={selectedIds.length === 0 || !selectedProcessedFile || generationStatus.includes('正在')}>
                    为选中项生成评论 ({selectedIds.length})
                  </button>
                )}
                {generationStatus && <p>{generationStatus}</p>}
                <DataTable
                  data={tableData}
                  onShowOrbit={handleShowOrbit}
                  onShowGrp={handleShowGrp}
                  onSelectionChange={setSelectedIds}
                />
              </div>
            )}
          </>
        )}
        {activeTab === '国内' && <GuoNei selectedProcessedFile={selectedProcessedFile} />}
        {activeTab === '自家网络设置' && (
          <Settings onProcessedFileSelect={setSelectedProcessedFile} />
        )}
        <InfoModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setModalData(null); // Clear data when modal closes
            setModalTitle(''); // Clear title
            setModalDownloadUrl(undefined); // Clear download URL
          }}
          title={modalTitle}
          data={modalData}
          downloadUrl={modalDownloadUrl}
        />
      </main>
    </div>
  );
}

export default App;

